import React, { useCallback, useContext, useRef, useState } from 'react';
import { FlatList, Platform, Pressable, StyleSheet, } from 'react-native';
import { withTheme, Text, useThemeMode } from '@rneui/themed';
import Animated, { useAnimatedStyle, useSharedValue, withTiming, } from 'react-native-reanimated';
import { CarouselSlide } from './CarouselSlide';
import { FAIcon, View } from '../../elements';
import { CAROUSEL_ITEMS } from '../../../utils/mocks';
import { IconButton, NavigationContext, useReactiveThemeProperty, useTheme } from '../../..';
import { CarouselDisplayContainer as Container, } from './CarouselDisplayContainer';
import { CarouselDisplayMemoizer } from './CarouselDisplay';
const UnthemedCarouselDisplay = ({ index: carouselIdx, collection, items = [], isLoading = false, isLoadingPage = false, hasNextPage = false, fetchNextPage, containerStyle = {}, titleContainerStyle = {}, titleStyle = {}, titleChevronStyle = {}, flatListStyle = {}, scrollControlContainerStyle, scrollControlIconStyle, }) => {
    const { onNavigateToItem: onPress } = useContext(NavigationContext);
    const flatListRef = useRef(null);
    const horizontalGutter = useReactiveThemeProperty('horizontalGutterCarousel');
    const { defaultCardHeight } = useTheme();
    const [lastPosition, setLastPosition] = useState(0);
    const [scrollOffset, setScrollOffset] = useState(400);
    const [contentWidth, setContentWidth] = useState();
    const onScrollNext = useCallback(() => {
        setLastPosition((lp) => {
            const offset = lp + (scrollOffset * .8);
            flatListRef.current?.scrollToOffset({ offset, animated: true });
            return lp;
        });
    }, [scrollOffset]);
    const onScrollPrev = useCallback(() => {
        setLastPosition((lp) => {
            const offset = lp - (scrollOffset * .8);
            flatListRef.current?.scrollToOffset({ offset, animated: true });
            return lp;
        });
    }, [scrollOffset]);
    const displayHoverControls = useSharedValue(0);
    const hoverControlStyle = useAnimatedStyle(() => {
        return {
            opacity: withTiming(displayHoverControls.value, {
                duration: 360,
            }),
            display: displayHoverControls.value === 0 ? 'none' : 'flex',
            zIndex: 1,
        };
    });
    const setDisplayHoverControls = useCallback((val) => {
        displayHoverControls.value = val ? 1 : 0;
    }, []);
    const keyExtractor = useCallback((item) => `${item.id}`, []);
    const renderItem = useCallback((props) => {
        const { index, item: { item } } = props;
        return (React.createElement(CarouselSlide, { index: carouselIdx, item: item, padLeft: index > 0, heightConstraint: defaultCardHeight }));
    }, [defaultCardHeight]);
    const onEndReached = useCallback(() => {
        if (hasNextPage && fetchNextPage)
            fetchNextPage();
    }, [hasNextPage, fetchNextPage]);
    const data = items;
    if (isLoadingPage && data.length > 0 && data[data.length - 1]?.id > 0) {
        data.push(CAROUSEL_ITEMS[0]);
    }
    else if ((isLoading || isLoadingPage) && data.length <= 0) {
        data.push(...CAROUSEL_ITEMS);
    }
    const isTitleDisplayable = collection?.name && data.length > 0;
    return (React.createElement(Container, { style: containerStyle, onDisplayScroll: setDisplayHoverControls },
        (isTitleDisplayable || isLoading) && (React.createElement(View, { style: titleContainerStyle },
            React.createElement(View, { style: {
                    paddingHorizontal: horizontalGutter,
                    backgroundColor: 'transparent',
                    maxWidth: '100%',
                } },
                React.createElement(Pressable, { style: { flexDirection: 'row', alignItems: 'center' }, onPress: () => collection && onPress(collection) },
                    React.createElement(Text, { style: titleStyle, numberOfLines: 2 }, isLoading ? ' ' : collection?.name || ''),
                    React.createElement(Animated.View, { style: hoverControlStyle },
                        React.createElement(FAIcon, { name: "chevron-right", style: [
                                titleStyle,
                                titleChevronStyle,
                            ] })))))),
        React.createElement(View, { style: styles.scrollContainer },
            lastPosition >= 10 && (React.createElement(Animated.View, { style: hoverControlStyle },
                React.createElement(IconButton, { containerStyle: scrollControlContainerStyle, style: scrollControlIconStyle, activeIconName: "chevron-left", onPress: () => onScrollPrev(), disabled: lastPosition < 10 }))),
            (contentWidth === undefined || contentWidth > scrollOffset) && (React.createElement(Animated.View, { style: hoverControlStyle },
                React.createElement(IconButton, { containerStyle: [
                        scrollControlContainerStyle,
                        {
                            right: 0,
                            paddingLeft: 40,
                        }
                    ], style: scrollControlIconStyle, activeIconName: "chevron-right", onPress: () => onScrollNext() }))),
            React.createElement("div", { className: "carousel-collection-list-container" },
                React.createElement(FlatList, { ref: flatListRef, horizontal: true, style: [
                        flatListStyle,
                        { paddingLeft: horizontalGutter },
                        styles.hoverOffsetStyle,
                    ], data: data, keyExtractor: keyExtractor, renderItem: renderItem, onEndReached: onEndReached, onContentSizeChange: (width) => setContentWidth(width), onLayout: ({ nativeEvent: { layout: { width } } }) => setScrollOffset(width), onScroll: ({ nativeEvent: { contentOffset: { x } } }) => setLastPosition(x), onScrollToIndexFailed: () => { } })))));
};
export const MemoizedCarouselDisplay = React.memo(UnthemedCarouselDisplay, CarouselDisplayMemoizer);
export const ThemeAwareMemoizedCarouselDisplay = (props) => {
    const { mode } = useThemeMode();
    return React.createElement(MemoizedCarouselDisplay, { mode: mode, ...props });
};
export const CarouselDisplay = withTheme(ThemeAwareMemoizedCarouselDisplay, 'CarouselDisplay');
const styles = StyleSheet.create({
    hoverOffsetStyle: Platform.select({
        web: {
            marginTop: -15,
            paddingTop: 15,
        },
        default: {},
    }),
    scrollContainer: {
        zIndex: 2,
        position: 'relative',
        backgroundColor: 'transparent',
    },
});
//# sourceMappingURL=CarouselDisplay.web.js.map